import {
  MixpanelTokenFragment,
  MixpanelProjectFragment,
  MixpanelArticleFragment,
  MixpanelOobTokenFragment,
  MixpanelArtistEditorialPageFragment,
  MixpanelExhibitionFragment
} from '@/gql/generated/graphql'
import {
  trackGenericButtonClickEvent,
  trackGenericSelectionMadeEvent,
  trackSearchEventFlow,
  trackPageEvent,
  trackPurchaseFlowEvent
} from 'analytics-utils/src'
import { UserInfo } from 'analytics-utils/src/types'

export enum TrackedButtonNames {
  testButton = 'Test Button',
  viewImage = 'View Image',
  openMarketplace = 'Open Marketplace',
  openMarketplaceProject = 'Open Marketplace Project',
  fullScreen = 'Full Screen',
  openSoloViewer = 'Open Solo Viewer',
  closeSoloViewer = 'Close Solo Viewer',
  paginateLeft = 'Paginate Left',
  paginateRight = 'Paginate Right',
  openArticleLink = 'Open Article Link',
  openExhibitionLink = 'Open Exhibition Link',
  openProjectLink = 'Open Project Link',
  openProjectPurchaseLink = 'Open Project Purchase Link',
  soloViewTab = 'Solo View Tab',
  toggleLiveView = 'Toggle Live View',
  toggleDisplayNotes = 'Toggle Display Notes',
  socialLink = 'Social link',
  similarArtist = 'Similar Artist',
  mainArtistProject = 'Artist Main Project Section',
  otherArtistProject = 'Artist Other Project Section',
  artistArticle = 'Artist Article Section',
  artistExhibition = 'Artist Exhibition Section',
  artistPress = 'Artist Press Section',
  projectCard = 'Project Card',
  clearFilters = 'Clear Filters',
  toggleFilterPanel = 'Toggle Filter Panel',
  filterOption = 'Filter Option',
  artistGridViewToggle = 'Artist Grid View Toggle',
  articleShareButton = 'Article Share Button',
  collectionHeaderGridViewToggle = 'Collection Header Grid View Toggle',
  addToCalendarOpened = 'Add to Calendar Modal Opened',
  addToCalendarConfirmed = 'Add to Calendar Confirmed',
  mpkozHeaderCTA = 'Mpkoz Header CTA',
  mpkozInquiry = 'Mpkoz Inquiry',
  mpkozInquiryConfirm = 'Mpkoz Inquiry Confirm',
  mpkozLampAboutCTA = 'Mpkoz Lamp About CTA',
  mpkozDigitalAboutCTA = 'Mpkoz Digital About CTA',
  mpkozProcessLearnMore = 'Mpkoz Process Learn More',
  mpkozArtistLearnMore = 'Mpkoz Artist Learn More',
  mpkozCreatorPurchase = 'Mpkoz Creator Purchase',
  mpkozCreatorSaveLamp = 'Mpkoz Save Lamp',
  mpkozCreatorCreateNewLamp = 'Mpkoz Create New Lamp',
  mpkozCreatorTakeAScreenshot = 'Mpkoz Take A Screenshot',
  bidsListOrderToggle = 'Bids List Order Toggle',
  bidsListExpand = 'Bids List Expand',
  bidsListConnectWallet = 'Bids List Connect Wallet',
  ramFlowConnectWallet = 'RAM Flow Connect Wallet',
  ramFlowPlaceNewBid = 'RAM Flow Place New Bid',
  ramFlowIncreaseBid = 'RAM Flow Increase Bid',
  ramFlowTopUpBidSelected = 'RAM Flow Top Up Bid Selected',
  ramFlowBidSubmitted = 'RAM Flow Bid Submitted',
  purchaseFlowConnectWallet = 'Purchase Flow Connect Wallet',
  purchaseFlowReturnToStartFromError = 'Purchase Flow Return To Start From Error',
  purchaseFlowPurchaseAgain = 'Purchase Flow Purchase Again',
  purchaseFlowPurchaseToAddressChange = 'Purchase Flow Purchase To Address Change',
  purchaseFlowPurchase = 'Purchase Flow Purchase',
  purchaseFlowPurchaseTo = 'Purchase Flow Purchase To',
  notifyMeFormSubscribe = 'Notify Me Form Subscribe',
  notifyMeFormUnsubscribe = 'Notify Me Form Unsubscribe',
  exhibitionPopUpCTA = 'Exhibition Pop Up CTA',
  exhibitionMoreInfo = 'Exhibition More Info',
  exhibitionCollectionLearnMore = 'Exhibition Collection Learn More',
  exhibitionArtistLearnMore = 'Exhibition Artist Learn More',
  exhibitionBehindTheExhibitionLearnMore = 'Exhibition Behind The Exhibition Learn More',
  bentoItem = 'Bento Item',
  homeViewAllCollections = 'Home View All Collections',
  homeAboutTokenImage = 'Home About Token Image',
  homeViewMarketplace = 'Home View Marketplace',
  recentListingToken = 'Recent Listing Token',
  discoverVertical = 'Discover Vertical',
  navItem = 'Nav Item',
  artistLearnMore = 'Artist Learn More',
  heroProject = 'Hero Project',
  heroExhibition = 'Hero Exhibition',
  heroProjectPurchase = 'Hero Project Purchase',
  busiestMagnifier = 'Open Busiest Magnifier',
  busyExploreAlgorithm = 'Busy Explore Algorithm',
  busyWorkPurchaseCTA = 'Busy Work Purchase CTA',
  busyClaimPlot = 'Busy Claim Plot',
  busyVideoPlay = 'Busy Video Play',
  busyMoreInformation = 'Busy More Information',
  buyNow = 'Secondary Buy Now',
  makeOffer = 'Make Offer',
  acceptOffer = 'Accept Offer',
  cancelListing = 'Cancel Listing',
  createListing = 'Create Listing',
  editListing = 'Edit Listing',
  galleryFrame = 'Gallery Frame Toggle',
  exportToken = 'Export Token',
  openLiveView = 'Open Live View',
  closeTokenPage = 'Close Token Page',
  openArtistLink = 'Open Artist Link',
  tokenGridItem = 'Token Grid Item',
  exploreGrid = 'Explore Grid',
  togglePrices = 'Toggle Prices',
  savePmpCustomization = 'Save PMP Customization',
  undoPmpCustomization = 'Undo PMP Customization',
  calendarGridItem = 'Calendar Grid Item',
  gasWarsPurchaseCTA = 'Gas Wars Purchase CTA',
  gasWarsGridItem = 'Gas Wars Grid Item',
  gasWarsConnectWallet = 'Gas Wars Connect Wallet',
  gasWarsModalViewToken = 'Gas Wars Modal View Token',
  viewCollection = 'View Collection',
  modalClose = 'Modal Close'
  // Add new button names here
}

type OmitTypename<T> = Omit<T, '__typename'>

type TrackedButtonProps = {
  // Add button props here
  token?: OmitTypename<MixpanelTokenFragment>
  oobToken?: OmitTypename<MixpanelOobTokenFragment>
  project?: OmitTypename<MixpanelProjectFragment>
  article?: OmitTypename<MixpanelArticleFragment>
  exhibition?: OmitTypename<MixpanelExhibitionFragment>
  artistPage?: OmitTypename<MixpanelArtistEditorialPageFragment> | null
  artistSlug?: string
  customProperties?: {
    [key: string]: any
  }
}

export const trackButtonClicked = (name: TrackedButtonNames, props?: TrackedButtonProps): void => {
  trackGenericButtonClickEvent<TrackedButtonNames, TrackedButtonProps>(name, props)
}

export enum TrackedSelectionNames {
  sortOrder = 'Sort Order',
  bidsDisplayTab = 'Bids Display Tab',
  tokenPageTab = 'Token Page Tab'
}

type TrackedSelectionProps = {
  selection: string
  options: string[]
  customProperties?: {
    [key: string]: any
  }
}

export const trackSelectionMade = (name: TrackedSelectionNames, props: TrackedSelectionProps): void => {
  trackGenericSelectionMadeEvent<TrackedSelectionNames, TrackedSelectionProps>(name, props.selection, props)
}

export enum TrackedPurchaseFlowNames {
  gasWars = 'Gas Wars Claim Minter',
  ram = 'Ranked Auction'
}

export enum TrackedPurchaseFlowStep {
  Idle = 'Idle', // Initial state
  WaitingConfirmation = 'Waiting Confirmation', // Wallet transaction is waiting for user to confirm
  Processing = 'Processing', // Transaction is being processed
  Success = 'Success', // Transaction is successful
  Error = 'Error' // Transaction is errored
}

type TrackedPurchaseFlowProps = {
  step: TrackedPurchaseFlowStep
  project?: OmitTypename<MixpanelProjectFragment>
  customProperties?: {
    [key: string]: any
  }
}

export const trackPurchaseFlow = (name: TrackedPurchaseFlowNames, props: TrackedPurchaseFlowProps): void => {
  trackPurchaseFlowEvent<TrackedPurchaseFlowNames, TrackedPurchaseFlowProps>(name, props)
}

export enum AnalyticsSearchEventStep {
  searchButtonClicked = 'search button clicked',
  resultClicked = 'full site search result clicked',
  ended = 'full site search ended'
}

export const trackSearchEvent = ({
  step,
  searchTerm,
  searchResult
}: {
  step: AnalyticsSearchEventStep
  searchTerm?: string
  searchResult?: string
  user?: UserInfo
}): void => {
  trackSearchEventFlow<AnalyticsSearchEventStep>(step, searchTerm, searchResult)
}

export enum AnalyticsPageNames {
  home = 'Home',
  collectionPage = 'Collection',
  discover = 'Discover',
  discoverVertical = 'Discover Vertical',
  collectionPurchase = 'Collection Purchase',
  artistsGrid = 'Artists grid',
  artistPage = 'Artist',
  error = 'Error',
  articlesGrid = 'Articles grid',
  articlePage = 'Article',
  exhibitionsGrid = 'Exhibitions grid',
  exhibitionPage = 'Exhibition',
  creatorPage = 'Creator',
  tokenPage = 'Token',
  secondaryMarketplace = 'Secondary Marketplace',
  calendar = 'Calendar',
  login = 'Login',
  profile = 'Profile',
  profileSets = 'Profile Sets',
  profileSetDetail = 'Profile Set Detail',
  profileAchievements = 'Profile Achievements',
  profileSettings = 'Profile Settings',
  timeline = 'Timeline'
}

type AnalyticsCollectionPageProps = {
  projectId: string
}

type AnalyticsArtistPageProps = {
  slug: string
}

type AnalyticsArticlePageProps = {
  slug: string
}

type AnalyticsExhibitionPageProps = {
  slug: string
}
type AnalyticsErrorPageProps = {
  errorType: number
}

type AnalyticsTokenPageProps = {
  tokenId: string
}

export type AnalyticsPageProps =
  | AnalyticsCollectionPageProps
  | AnalyticsArtistPageProps
  | AnalyticsErrorPageProps
  | AnalyticsArticlePageProps
  | AnalyticsExhibitionPageProps
  | AnalyticsTokenPageProps

export const trackPage = (pageName: AnalyticsPageNames): void => {
  trackPageEvent(pageName)
}

// For backward compatibility
export const MixpanelPageNames = AnalyticsPageNames
export const MixpanelSearchEventStep = AnalyticsSearchEventStep
export type MixpanelPageProps = AnalyticsPageProps
